import { useState } from 'react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'

import { ReactComponent as ExpandIcon } from '../../assets/icons/IconExpand.svg'
import {
  Spinner,
  BarLoader,
  StatusRow,
  StatusChevron,
  Checklist,
  ChecklistItem,
  ChecklistIcon,
  CheckBadge,
} from './styled'

// Filled circle + white check; the circle color comes from CheckBadge's color.
const CheckGlyph = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' aria-hidden='true'>
    <circle cx='10' cy='10' r='8' fill='currentColor' />
    <path
      d='M6.2 10.4l2.4 2.4 5.2-5.6'
      fill='none'
      stroke='#ffffff'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/*
 * Streaming progress for a loading bot message:
 *   - Before any status arrives: an indeterminate bar.
 *   - Once a step is known: the current step (spinner + label) with an expand
 *     chevron. The whole row is one touch target that expands a checklist of
 *     every step so far — all checked off except the current (spinner) step.
 * Observer so it re-renders as new status updates stream in.
 */
const ToolStatus = observer(({ currentStatus, statusUpdates = [] }) => {
  const [open, setOpen] = useState(false)

  if (!currentStatus?.update) return <BarLoader aria-label='Loading response' />

  // Collapse consecutive duplicates into the step history.
  const history = []
  statusUpdates.forEach(s => {
    if (s?.update && history[history.length - 1] !== s.update) history.push(s.update)
  })
  const expandable = history.length > 1

  return (
    <div>
      {/* Pinned to the bubble's top-right corner; toggles the checklist. */}
      {expandable && (
        <StatusChevron
          type='button'
          open={open}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? 'Collapse steps' : 'Show all steps'}>
          <ExpandIcon />
        </StatusChevron>
      )}
      {/* Collapsed: current step only. Expanded: it moves to the list bottom. */}
      {!open && (
        <StatusRow
          type='button'
          expandable={expandable}
          onClick={() => expandable && setOpen(o => !o)}
          aria-expanded={expandable ? open : undefined}>
          <Spinner />
          <span className='hn-status-label'>{currentStatus.update}</span>
        </StatusRow>
      )}
      {open && expandable && (
        <Checklist>
          {history.map((text, i) => {
            const isCurrent = i === history.length - 1
            return (
              <ChecklistItem key={`${text}-${i}`}>
                <ChecklistIcon>
                  {isCurrent ? (
                    <Spinner />
                  ) : (
                    <CheckBadge>
                      <CheckGlyph />
                    </CheckBadge>
                  )}
                </ChecklistIcon>
                <span>{text}</span>
              </ChecklistItem>
            )
          })}
        </Checklist>
      )}
    </div>
  )
})

ToolStatus.propTypes = {
  currentStatus: PropTypes.shape({ update: PropTypes.string, type: PropTypes.string }),
  statusUpdates: PropTypes.arrayOf(PropTypes.object),
}

export default ToolStatus
